import { useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { api, resolveImg } from "@/lib/api";
import { Crosshair, Flame, LogOut, Radar, Inbox, CalendarClock } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import ProfilePanel from "@/components/ProfilePanel";

export default function Navbar({ view, setView, receivedCount }) {
  const { user, setUser, logout } = useAuth();
  const [saving, setSaving] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);

  const toggleCamp = async (val) => {
    setSaving(true);
    try {
      const { data } = await api.patch("/profile/camp-mode", { camp_mode: val });
      setUser(data.user);
      toast.success(val ? "Camp Mode ON — you're floated to the top." : "Camp Mode off.");
    } catch {
      toast.error("Could not update Camp Mode.");
    } finally {
      setSaving(false);
    }
  };

  const NavBtn = ({ id, icon: Icon, label, badge }) => (
    <button
      data-testid={`nav-${id}`}
      onClick={() => setView(id)}
      className={`relative flex items-center gap-2 px-4 py-2 rounded-sm font-heading uppercase tracking-wider text-sm transition-colors duration-200 ${
        view === id ? "bg-volt text-black font-bold" : "text-zinc-400 hover:text-white"
      }`}
    >
      <Icon size={16} /> <span className="hidden sm:inline">{label}</span>
      {badge > 0 && (
        <span className="absolute -top-1.5 -right-1.5 bg-[#FF3B30] text-white text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center">
          {badge}
        </span>
      )}
    </button>
  );

  return (
    <>
    <header className="sticky top-0 z-40 bg-[#0d0d0d]/95 backdrop-blur border-b border-[#27272a]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <Crosshair className="text-volt" size={24} strokeWidth={2.5} />
          <span className="font-heading font-black text-xl uppercase tracking-tight hidden sm:block">
            Spar<span className="text-volt">Base</span>
          </span>
        </div>

        <nav className="flex items-center gap-1 bg-[#161616] border border-[#27272a] rounded-sm p-1">
          <NavBtn id="radar" icon={Radar} label="Radar" />
          <NavBtn id="camp" icon={Flame} label="My Camp" />
          <NavBtn id="sessions" icon={CalendarClock} label="Sessions" />
          <NavBtn id="requests" icon={Inbox} label="Requests" badge={receivedCount} />
        </nav>

        <div className="flex items-center gap-3">
          <motion.div
            animate={user?.camp_mode ? { boxShadow: "0 0 14px rgba(0,255,102,0.5)" } : { boxShadow: "0 0 0 rgba(0,0,0,0)" }}
            className={`hidden md:flex items-center gap-2 px-3 py-1.5 rounded-sm border ${
              user?.camp_mode ? "border-volt bg-volt/10" : "border-[#27272a] bg-[#161616]"
            }`}
          >
            <Flame size={15} className={user?.camp_mode ? "text-volt" : "text-zinc-500"} />
            <span className="text-xs uppercase tracking-wider text-zinc-300">Camp</span>
            <Switch
              data-testid="camp-mode-toggle"
              checked={!!user?.camp_mode}
              disabled={saving}
              onCheckedChange={toggleCamp}
              className="data-[state=checked]:bg-volt"
            />
          </motion.div>

          <button
            data-testid="open-profile-btn"
            onClick={() => setProfileOpen(true)}
            className="flex items-center gap-2 group"
          >
            <div className="w-9 h-9 rounded-xl overflow-hidden border border-[#27272a] bg-[#0d0d0d] flex items-center justify-center group-hover:border-volt transition-colors duration-200">
              {user?.avatar_url ? (
                <img src={resolveImg(user.avatar_url)} alt="me" className="w-full h-full object-cover" />
              ) : (
                <span className="font-heading text-lg text-zinc-500">{user?.fighter_name?.[0] || "?"}</span>
              )}
            </div>
            <div className="text-left hidden lg:block">
              <div className="font-heading uppercase text-sm leading-none group-hover:text-volt transition-colors duration-200">{user?.fighter_name}</div>
              <div className="text-[11px] text-zinc-500">{user?.gym_name}</div>
            </div>
          </button>

          <button
            data-testid="logout-btn"
            onClick={logout}
            className="w-9 h-9 flex items-center justify-center border border-[#27272a] rounded-xl text-zinc-400 hover:text-[#FF3B30] hover:border-[#FF3B30] transition-colors duration-200"
          >
            <LogOut size={16} />
          </button>
        </div>
      </div>

      {/* mobile camp toggle */}
      <div className="md:hidden max-w-7xl mx-auto px-4 pb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Flame size={15} className={user?.camp_mode ? "text-volt" : "text-zinc-500"} />
          <span className="text-xs uppercase tracking-wider text-zinc-300">Camp Mode</span>
        </div>
        <Switch
          checked={!!user?.camp_mode}
          disabled={saving}
          onCheckedChange={toggleCamp}
          className="data-[state=checked]:bg-volt"
        />
      </div>
    </header>
    <ProfilePanel open={profileOpen} onOpenChange={setProfileOpen} />
    </>
  );
}
